import React, { type ReactNode, useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { Label } from '@atlaskit/form';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { ConfluenceSpaceAri } from '@atlassian/ari/confluence';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	CONFLUENCE_PAGE_MOVE_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { useConfluenceEntities } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceEntities';
import type { ConfluencePageOption } from '@atlassian/rovo-platform-ui-components/base-page-selector/types';
import type { ConfluenceSpaceOption } from '@atlassian/rovo-platform-ui-components/base-space-selector/types';
import { ConfluencePageSelector } from '@atlassian/rovo-platform-ui-components/confluence-page-selector';
import { ConfluenceSpaceSelector } from '@atlassian/rovo-platform-ui-components/confluence-space-selector';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';

export const filterConfluencePageMoveActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.ConfluencePageMove]>[] => {
	const filteredActions = actions.filter(assertIsConfluencePageMoveAction);
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.ConfluencePageMove]
	>[];
};

const assertIsConfluencePageMoveAction = (action: MessageAction): MessageAction => {
	if (action.key !== CONFLUENCE_PAGE_MOVE_KEY) {
		throw new Error('Invalid action key for ConfluencePageMoveAction');
	}
	return action;
};

export const ConfluencePageMoveAction = ({
	onCustomDataConfirm,
	actions,
	getStatus,
	getReadonly,
	cloudId,
	aaid,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: ActionRendererFor<typeof ActionKeys.ConfluencePageMove>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const firstAction = actions[0];
	const { data } = firstAction;
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const { getEntity } = useConfluenceEntities(cloudId, { fetchHomepage: true });
	const [selectedPage, setSelectedPage] = useState<ConfluencePageOption | undefined>(undefined);
	const [selectedSpace, setSelectedSpace] = useState<ConfluenceSpaceOption | undefined>(undefined);
	const isDisabled = status !== 'idle' || readonly || !selectedPage;
	const isSubmitting = status === 'started';

	useEffect(() => {
		const fetchPage = async () => {
			if (data.targetId) {
				const page = await getEntity(data.targetId, 'page');
				const space = await getEntity(data.targetId, 'space');

				// Currently, the BE only returns one ID and we don't know if it's a page or spaceId so we need to check both and handle each case.
				if (page) {
					setSelectedPage({
						label: page.name,
						value: page.id,
						type: 'page',
					});

					if (page.space?.id) {
						const spaceId = ConfluenceSpaceAri.parse(page.space.id).spaceId;
						setSelectedSpace({
							label: page.space.name ?? '',
							value: spaceId,
							key: page.space.key ?? '',
							iconPath: page.space.icon?.path ?? '',
						});
					}
				} else if (space) {
					setSelectedSpace({
						label: space.name,
						value: space.id,
						key: space.key,
						iconPath: space.icon?.path,
					});

					setSelectedPage({
						label: space.homepage?.name ?? '',
						value: space.homepage?.id ?? '',
						type: 'page',
					});
				}
			}
		};

		fetchPage();
	}, [data, getEntity]);

	const handleSubmit = useCallback(() => {
		const mergedData = {
			...data,
			targetId: selectedPage?.value,
			contentType: selectedPage?.type ?? 'page',
		};

		if (
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			const plannedActions = actions.filter((action) => getStatus(action) === 'idle');

			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'confluencePageMoveRenderer',
				},
				confirmed_tools: {
					message_id: messageId,
					tools: plannedActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed', mergedData)
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		onCustomDataConfirm(firstAction, mergedData);
	}, [
		onCustomDataConfirm,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		data,
		selectedPage,
		firstAction,
		actions,
		getStatus,
	]);

	return (
		<Stack space="space.250" testId="confluence-page-add-label-action">
			<Text>
				{formatMessage(messages.actionMessage, {
					name: selectedPage?.label ?? '',
					bold: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
				})}
			</Text>
			<ActionCardWrapper
				title={formatMessage(messages.confluencePageMoveActionCardTitle)}
				product="confluence"
			>
				<Stack space="space.100">
					<Box>
						<Label htmlFor="confluence-page-move-space-selector">
							{formatMessage(messages.spaceSelectorLabel)}
						</Label>
						<ConfluenceSpaceSelector
							cloudId={cloudId}
							userId={aaid}
							testId="confluence-page-move-space-selector"
							// If the editor is disabled then the selector should be disabled rather than hidden.
							onMenuOpen={() => {
								sendAnalyticsEvent({
									action: 'opened',
									actionSubject: 'dropdown',
									actionSubjectId: 'rovoConfluencePageMoveSpaceSelector',
								});
							}}
							isDisabled={isDisabled}
							isRequired
							onChange={(option: ConfluenceSpaceOption | null) => {
								if (option) {
									setSelectedSpace(option);
									setSelectedPage({
										label: option.homepage?.label ?? '',
										value: option.homepage?.value ?? '',
										type: option.homepage?.type ?? 'page',
									});
									sendAnalyticsEvent({
										action: 'selected',
										actionSubject: 'dropdown',
										actionSubjectId: 'rovoConfluencePageMoveSpaceSelector',
									});
								}
							}}
							fetchHomepage
							value={selectedSpace}
						/>
					</Box>
					<Box>
						<Label htmlFor="confluence-page-move-page-selector">
							{formatMessage(messages.pageSelectorLabel)}
						</Label>
						<ConfluencePageSelector
							cloudId={cloudId}
							testId="confluence-page-move-page-selector"
							contentTypes={['page', 'blogPost']}
							isDisabled={isDisabled || !selectedSpace || !selectedPage}
							// Using selectedSpaceId in the key causes ConfluencePageSelector to remount whenever the space is changed.
							// This clears the cache of dropdown options and refetches defaultOptions for that space.
							key={`pageSelector-${selectedSpace?.value}`}
							spaceId={selectedSpace?.value}
							onMenuOpen={() => {
								sendAnalyticsEvent({
									action: 'opened',
									actionSubject: 'dropdown',
									actionSubjectId: 'rovoConfluencePageMovePageSelector',
								});
							}}
							isRequired
							onChange={(option: ConfluencePageOption | null) => {
								if (option) {
									setSelectedPage(option);
									sendAnalyticsEvent({
										action: 'selected',
										actionSubject: 'dropdown',
										actionSubjectId: 'rovoConfluencePageMovePageSelector',
									});
								}
							}}
							value={selectedPage}
						/>
					</Box>
				</Stack>
				<ActionCardFooter>
					<Button
						appearance="primary"
						isDisabled={isDisabled}
						isLoading={isSubmitting}
						onClick={handleSubmit}
					>
						{formatMessage(messages.movePageButton)}
					</Button>
				</ActionCardFooter>
			</ActionCardWrapper>
		</Stack>
	);
};
